/*
  Pinned products on collection pages, for ad landing links.

    /collections/fw26?pin=kira-skirt-grey
    /collections/fw26?pin=kira-skirt-grey,kira-skirt-1     (several, in order)

  - Pinned products are shown first, in the order given. The rest of the
    collection keeps its normal order.
  - If a pinned product is further down the page, it moves to the top.
  - If it isn't on the page (later page, or not in the collection), its card
    is fetched from /products/<handle>?view=card and added at the top.
  - Works with every collection grid (plain grid and the editorial mosaics,
    desktop + mobile grids).
  - Filtering / sorting keeps the pin while the product matches the filter.
  - On later pages (?page=2...), the pinned products are removed so they
    don't show twice.

  Cards are never moved as-is: the theme's <product-card> re-binds its
  quick-add listeners every time it is attached, so a moved card would add to
  cart twice. Moved cards are replaced by fresh copies instead.
*/
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const STORE_KEY = `pin:${location.pathname}`;
const MAX_PINS = 8;

const cleanHandle = (value) =>
  decodeURIComponent(String(value || ''))
    .trim()
    .toLowerCase()
    .replace(/^.*\/products\//, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '');

function readPins() {
  const fromUrl = new URLSearchParams(location.search).get('pin');
  if (fromUrl) {
    const pins = fromUrl.split(',').map(cleanHandle).filter(Boolean).slice(0, MAX_PINS);
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(pins)); } catch (e) {}
    return { pins, remembered: false };
  }
  // No ?pin= in the link: pins remembered from the ad landing are only used to
  // avoid showing the pinned products again on page 2+.
  try {
    return { pins: JSON.parse(sessionStorage.getItem(STORE_KEY) || '[]'), remembered: true };
  } catch (e) {
    return { pins: [], remembered: true };
  }
}

const { pins: PINS, remembered: REMEMBERED } = readPins();
const cardHtml = new Map();

function fetchCard(handle) {
  if (!cardHtml.has(handle)) {
    cardHtml.set(
      handle,
      fetch(`${ROOT}products/${encodeURIComponent(handle)}?view=card`, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : null))
        .then((html) => (html && html.includes('<product-card') ? html : null))
        .catch(() => null)
    );
  }
  return cardHtml.get(handle);
}

function handleOf(card) {
  if (card.dataset.pinHandle) return card.dataset.pinHandle;
  const link = card.querySelector('.product-card--featured-image-link, .product-card-title');
  if (!link) return '';
  const match = new URL(link.getAttribute('href'), location.origin).pathname.match(/\/products\/([^/?#]+)/);
  const handle = match ? cleanHandle(match[1]) : '';
  card.dataset.pinHandle = handle;
  return handle;
}

function containersOf(grid) {
  const editorial = grid.querySelectorAll('.plp-editorial-grid');
  return editorial.length ? Array.from(editorial) : [grid];
}

function cardFromHtml(html) {
  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  // Make sure swatch behaviour loads even if no other card on the page had it.
  tpl.content.querySelectorAll('script[type="module"][src]').forEach((s) => {
    import(s.getAttribute('src')).catch(() => {});
    s.remove();
  });
  return tpl.content.querySelector('product-card');
}

// Copy a card that is already on the page. If lazysizes was still loading one
// of its images, the copy would keep the "lazyloading" class (blurred) but
// nothing would ever finish loading it. Reset those images to "lazyload" so
// lazysizes picks the copy up and loads it.
function cloneCard(card) {
  const copy = card.cloneNode(true);
  copy.querySelectorAll('.lazyloading').forEach((el) => {
    el.classList.remove('lazyloading');
    el.classList.add('lazyload');
  });
  return copy;
}

function arrange(container, pinnedHtml, mode) {
  const cards = Array.from(container.querySelectorAll('product-card'));
  if (!cards.length) return;
  const slots = cards.map((c) => c.parentElement);

  const pinned = [];
  const used = new Set();
  if (mode !== 'dedupe') {
    PINS.forEach((handle) => {
      let card = cards.find((c) => handleOf(c) === handle);
      if (!card && mode === 'inject' && pinnedHtml[handle]) card = cardFromHtml(pinnedHtml[handle]);
      if (card) {
        pinned.push(card);
        used.add(handle);
      }
    });
  }
  // Later pages: drop pinned products entirely (they were shown on page 1).
  const drop = new Set(mode === 'dedupe' ? PINS : used);
  const seen = new Set();
  const rest = cards.filter((c) => {
    const h = handleOf(c);
    if (drop.has(h)) return false;
    if (seen.has(h)) return false; // duplicate from "load more"
    seen.add(h);
    return true;
  });
  const order = [...pinned, ...rest];

  const unchanged = order.length === cards.length && order.every((c, i) => c === cards[i]);
  if (unchanged) return;

  // Extra slot(s) at the end when a card was added from outside the page.
  while (slots.length < order.length) {
    const last = slots[slots.length - 1];
    const slot = last.cloneNode(false);
    last.after(slot);
    slots.push(slot);
  }

  const placed = order.map((card, i) => {
    if (card === cards[i]) return card; // already in the right slot
    return cards.includes(card) ? cloneCard(card) : card;
  });
  cards.forEach((card, i) => {
    if (placed[i] !== card) card.remove();
  });
  placed.forEach((card, i) => {
    if (card.parentElement !== slots[i]) slots[i].appendChild(card);
  });
  slots.slice(placed.length).forEach((slot) => slot.remove());
}

let busy = false;
let observer = null;
async function apply() {
  const grid = document.getElementById('product-grid');
  if (!grid || !PINS.length || busy) return;

  const params = new URLSearchParams(location.search);
  const page = parseInt(params.get('page') || '1', 10);
  const filtered = Array.from(params.keys()).some((k) => k.startsWith('filter.'));
  const mode = page > 1 ? 'dedupe' : filtered ? 'move' : 'inject';
  if (REMEMBERED && mode !== 'dedupe') return;

  const pinnedHtml = {};
  if (mode === 'inject') {
    const onPage = new Set(Array.from(grid.querySelectorAll('product-card')).map(handleOf));
    await Promise.all(
      PINS.filter((h) => !onPage.has(h)).map(async (h) => {
        pinnedHtml[h] = await fetchCard(h);
      })
    );
  }

  const current = document.getElementById('product-grid');
  if (!current) return;
  busy = true;
  try {
    containersOf(current).forEach((container) => arrange(container, pinnedHtml, mode));
    if (observer) observer.takeRecords(); // ignore our own DOM changes
  } finally {
    busy = false;
  }
}

if (PINS.length) {
  apply();
  // Re-apply after filtering, sorting or "load more" re-render the grid.
  const root = document.getElementById('ProductGridContainer') || document.body;
  let timer;
  observer = new MutationObserver(() => {
    if (busy) return;
    clearTimeout(timer);
    timer = setTimeout(apply, 60);
  });
  observer.observe(root, { childList: true, subtree: true });
}
